import type { QuestionExportAnswer, QuestionSerialized } from '@repo/types/questions/Question';
import type { IQuestion } from '@repo/types/questions/Question.interface';
import type { QuizAttemptPrivate, QuizSection } from '@repo/types/quiz';

import { QuizDataResolver } from '@repo/lib/quiz/quizDataResolver';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { QuizResolverActions, QuizResolverState } from '.';

export const useQuizResolverStore = create<QuizResolverState & QuizResolverActions>()(
  persist(
    (set, get) => ({
      content: [],
      serializedContent: [],
      quizId: undefined,
      attemptId: undefined,
      localTimestamp: undefined,
      getQuestionJSON: <T extends QuestionSerialized>(sectionIndex: number, questionIndex: number): T => {
        const { serializedContent } = get();
        const result = serializedContent[sectionIndex]?.[questionIndex] as T;
        return result;
      },
      getQuestionInstance: <T extends IQuestion>(sectionIndex: number, questionIndex: number): T => {
        const { content } = get();
        return content[sectionIndex]?.[questionIndex] as T;
      },
      getExportedAnswers: (): QuestionExportAnswer[][] => {
        const { content } = get();
        return content.map((section) => section.map((question) => question.exportAnswer()) as QuestionExportAnswer[]);
      },
      updateQuestionAnswer: (sectionIndex, questionIndex, answerUserQuestion): void => {
        const { serializedContent, content } = get();
        const newSerializedContent: QuestionSerialized[][] = [...serializedContent];
        const newContent: IQuestion[][] = [...content];
        if (newSerializedContent[sectionIndex] && newContent[sectionIndex]) {
          newSerializedContent[sectionIndex] = [...newSerializedContent[sectionIndex]];
          newSerializedContent[sectionIndex][questionIndex] = answerUserQuestion;
          newContent[sectionIndex] = [...newContent[sectionIndex]];
          set({
            serializedContent: newSerializedContent,
            content: newContent,
            localTimestamp: new Date().toISOString(),
          });
        }
      },
      updateQuestionMarked: (attemptData: QuizAttemptPrivate): void => {
        const { serializedContent, content } = get();
        const { serializedContent: newSerializedContent, content: newContent } = QuizDataResolver.updateQuestionsMarkedStatus(serializedContent, content, attemptData.body.marked);

        set({
          serializedContent: newSerializedContent,
          content: newContent,
        });
      },
      syncWithAttemptData: (quizContent: QuizSection[], attemptData: QuizAttemptPrivate): void => {
        const { needsSync, serializedContent } = get();
        if (!needsSync(attemptData)) {
          const classResult: IQuestion[][] = QuizDataResolver.buildQuestionsFromLocalData(quizContent, serializedContent, attemptData);
          set({
            content: classResult,
          });
          return;
        }
        const { serializedResult, classResult } = QuizDataResolver.buildQuestionsFromApiData(quizContent, attemptData);

        set({
          content: classResult,
          serializedContent: serializedResult,
          localTimestamp: attemptData.updatedAt,
          attemptId: attemptData.id,
        });
      },
      clear: (): void => {
        set({
          serializedContent: [],
          localTimestamp: undefined,
          quizId: undefined,
          attemptId: undefined,
        });
      },
      needsSync: (attemptData: QuizAttemptPrivate): boolean => {
        const { localTimestamp, attemptId, quizId } = get();
        if (attemptId !== attemptData.id || quizId !== attemptData.quizId) {
          set({
            quizId: attemptData.quizId,
            attemptId: attemptData.id,
          });
          return true;
        } else if (!localTimestamp || !attemptData.updatedAt) {
          return true;
        }

        return new Date(attemptData.updatedAt) > new Date(localTimestamp);
      },
      updateLocalTimestamp: (timestamp: string, attemptId: string): void => {
        set({
          localTimestamp: timestamp,
          attemptId: attemptId,
        });
      },
    }),
    {
      name: 'quiz-resolver-store',
      partialize: (state) => ({
        serializedContent: state.serializedContent,
        quizId: state.quizId,
        attemptId: state.attemptId,
        localTimestamp: state.localTimestamp,
      }),
    }
  )
);
